import { i18n } from "../../i18n/i18n";

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import adminRoutes from '../../routes/adminRoutes'
import subscriberService from '../../services/subscriber.service'
import siteService from '../../services/site.service'
import permissionService from '../../services/permission.service'
import subscriberApi, { SubscriberData } from '../../apis/subscriber.api'
import { PermissionData } from '../../apis/permission.api'
import siteApi, { SiteData } from '../../apis/site.api'
import userApi, { UserCreateRequest } from '../../apis/user.api'
import ContentLayout from '../../components/ContentLayout'
import ContentMain from '../../components/ContentMain'
import Card from '../../components/Card'
import Row from '../../components/Row/Row'
import InputText from '../../components/InputText'
import CardButton from '../../components/CardButton'
import Select from '../../components/Select'
import Checkbox from '../../components/Checkbox'
import PermissionPicker from '../../components/PermissionPicker'
import PageHeading from '../../components/PageHeading'

interface UserCreateProps extends RouteComponentProps {}

interface UserCreateState {
  username: string
  subscriber: string
  site: string
  timeout: string
  password: string
  admin: boolean
  email: string
  phone: string
  notify: boolean
  permissions: PermissionData[]
  sites: SiteData[]
  subscribers: SubscriberData[]
  allowedPermissions: PermissionData[]
}

class UserCreate
  extends Component<UserCreateProps, UserCreateState> {
  constructor (props: UserCreateProps) {
    super(props)
    this.state = {
      username: '',
      subscriber: '',
      site: '',
      timeout: '',
      password: '',
      admin: false,
      email: '',
      phone: '',
      notify: true,
      permissions: [],
      sites: [],
      subscribers: [],
      allowedPermissions: []
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleCheckChange = this.handleCheckChange.bind(this)
    this.handlePermissionChange = this.handlePermissionChange.bind(this)
    this.handleCreateClick = this.handleCreateClick.bind(this)
  }

  get subscriberSelectOptions () {
    return subscriberService.selectOptions(this.state.subscribers)
  }

  get siteSelectOptions () {
    return siteService.selectOptions(this.state.sites)
  }

  get allowedPermissionNames () {
    const { allowedPermissions } = this.state
    return permissionService.listNames(allowedPermissions)
  }

  get permissionNames () {
    const { permissions } = this.state
    return permissionService.listNames(permissions)
  }

  create () {
    const { username, site, password, email, phone, notify } = this.state
    const { timeout, permissions } = this.state
    const permissionIds = permissionService.listIds(permissions)
    const payload: UserCreateRequest = {}
    if (username) payload.username = username
    if (site) payload.site = Number(site)
    if (password) payload.password = password
    if (email) payload.email = email
    if (phone) payload.phone = phone
    payload.notify = notify
    if (timeout) payload.timeout = Number(timeout)
    if (permissions) payload.permissions = permissionIds
    userApi.create(payload, user => {
      if (!user) return
      this.props.history.push(adminRoutes.users)
    })
  }

  listSites () {
    const subscriberId = Number(this.state.subscriber)
    subscriberApi.listSites(subscriberId, sites => {
      if (!sites) return
      this.setState({ sites })
    })
  }

  listAllowedPermissions () {
    const siteId = Number(this.state.site)
    siteApi.listPermissions(allowedPermissions => {
      if (!allowedPermissions) return
      this.setState({ allowedPermissions })
    }, siteId)
  }

  handleInputChange (name: string, value: string) {
    if (name === 'username') this.setState({ username: value })
    else if (name === 'subscriber') {
      this.setState({ subscriber: value }, () => this.listSites())
    } else if (name === 'site') {
      this.setState({ site: value }, () => this.listAllowedPermissions())
    } else if (name === 'timeout') this.setState({ timeout: value })
    else if (name === 'password') this.setState({ password: value })
    else if (name === 'email') this.setState({ email: value })
    else if (name === 'phone') this.setState({ phone: value })
  }

  handleCheckChange (name: string) {
    if (name === 'admin') {
      this.setState(state => {
        return {
          admin: !state.admin,
          permissions: state.allowedPermissions
        }
      })
    } else if (name === 'notify') {
      this.setState(state => {
        return { notify: !state.notify }
      })
    }
  }

  handlePermissionChange (permissionNames: string[]) {
    const { allowedPermissions } = this.state
    const permissions = permissionService.filterByNames(
      allowedPermissions,
      permissionNames
    )
    this.setState({ permissions })
  }

  handleCreateClick () {
    this.create()
  }

  listSubscribers () {
    subscriberApi.list(subscribers => {
      if (!subscribers) return
      this.setState({ subscribers })
    })
  }

  componentDidMount () {
    this.listSubscribers()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading text={i18n("Create User")} />
            <Row column={3}>
              <InputText
                name="username"
                value={this.state.username}
                label={i18n("Username")}
                onChange={this.handleInputChange} />
              <Select
                name="subscriber"
                value={this.state.subscriber}
                label={i18n("Subscriber")}
                options={this.subscriberSelectOptions}
                onChange={this.handleInputChange} />
              <Select
                name="site"
                value={this.state.site}
                label={i18n("Site")}
                options={this.siteSelectOptions}
                onChange={this.handleInputChange} />
            </Row>
            <Row column={3}>
              <InputText
                name="timeout"
                value={this.state.timeout}
                label={i18n("Timeout")}
                onChange={this.handleInputChange} />
              <InputText
                name="password"
                value={this.state.password}
                label={i18n("Password")}
                onChange={this.handleInputChange} />
              <Checkbox
                name="admin"
                label="Admin (Full access)"
                checked={this.state.admin}
                align="center"
                onChange={this.handleCheckChange} />
            </Row>
            <Row column={3}>
              <InputText
                name="email"
                value={this.state.email}
                label={i18n("Email")}
                onChange={this.handleInputChange} />
              <InputText
                name="phone"
                value={this.state.phone}
                label={i18n("Phone")}
                onChange={this.handleInputChange} />
              <Checkbox
                name="notify"
                label="Notify user via mail"
                checked={this.state.notify}
                align="center"
                onChange={this.handleCheckChange} />
            </Row>
            <PermissionPicker
              allowed={this.allowedPermissionNames}
              permissions={this.permissionNames}
              onChange={this.handlePermissionChange} />
            <CardButton
              proceedText={i18n("Create")}
              cancelText={i18n("Cancel")}
              onProceedClick={this.handleCreateClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}

export default withRouter(UserCreate)
